import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { appId, db } from '@/lib/firebase';

interface AnalyticsSettings {
  gtmContainerId?: string | null;
  gtmEnabled?: boolean;
  ga4Enabled?: boolean;
  ga4MeasurementId?: string | null;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;
const GA4_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/i;
const GTM_SCRIPT_ID = 'smbprime-gtm-script';
const GTM_NOSCRIPT_ID = 'smbprime-gtm-noscript';
const GA4_SCRIPT_ID = 'smbprime-ga4-script';
const GA4_INLINE_SCRIPT_ID = 'smbprime-ga4-inline';

function removeGtmScript(): void {
  const existingScript = document.getElementById(GTM_SCRIPT_ID);
  if (existingScript) {
    existingScript.remove();
  }
  const existingNoscript = document.getElementById(GTM_NOSCRIPT_ID);
  if (existingNoscript) {
    existingNoscript.remove();
  }
}

function injectGtmScript(containerId: string): void {
  const existingScript = document.getElementById(GTM_SCRIPT_ID) as HTMLScriptElement | null;
  const existingContainerId = existingScript?.dataset.gtmContainerId;
  if (existingContainerId === containerId) {
    return;
  }

  removeGtmScript();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.dataset.gtmContainerId = containerId;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.id = GTM_NOSCRIPT_ID;
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(containerId)}`;
  iframe.setAttribute('height', '0');
  iframe.setAttribute('width', '0');
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function removeGa4Script(): void {
  const existing = document.getElementById(GA4_SCRIPT_ID);
  if (existing) existing.remove();
  const existingInline = document.getElementById(GA4_INLINE_SCRIPT_ID);
  if (existingInline) existingInline.remove();
}

/**
 * Inject GA4 using the same pattern as Google's install snippet so the first
 * page_view is sent when gtag.js loads (triggers collect to google-analytics.com).
 */
function injectGa4Script(measurementId: string): void {
  const existing = document.getElementById(GA4_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing?.dataset.ga4MeasurementId === measurementId) return;

  removeGa4Script();

  const escapedId = measurementId.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  const inline = document.createElement('script');
  inline.id = GA4_INLINE_SCRIPT_ID;
  inline.dataset.ga4MeasurementId = measurementId;
  inline.textContent = [
    'window.dataLayer = window.dataLayer || [];',
    'function gtag(){dataLayer.push(arguments);}',
    "gtag('js', new Date());",
    `gtag('config', '${escapedId}', { send_page_view: true });`,
  ].join('\n');
  document.head.appendChild(inline);

  const script = document.createElement('script');
  script.id = GA4_SCRIPT_ID;
  script.async = true;
  script.dataset.ga4MeasurementId = measurementId;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function GoogleTagManager(): null {
  const location = useLocation();
  const [ga4MeasurementId, setGa4MeasurementId] = useState<string | null>(null);
  const ga4InitialSentRef = useRef(false);

  useEffect(() => {
    const analyticsRef = doc(db, 'artifacts', appId, 'settings', 'analytics');

    const unsubscribe = onSnapshot(
      analyticsRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          removeGtmScript();
          setGa4MeasurementId(null);
          ga4InitialSentRef.current = false;
          removeGa4Script();
          return;
        }

        const analyticsSettings = docSnap.data() as AnalyticsSettings;

        const gtmEnabled = analyticsSettings.gtmEnabled === true;
        const gtmContainerId = (analyticsSettings.gtmContainerId || '').trim().toUpperCase();

        if (gtmEnabled && gtmContainerId && GTM_CONTAINER_ID_PATTERN.test(gtmContainerId)) {
          injectGtmScript(gtmContainerId);
        } else {
          removeGtmScript();
        }

        const ga4Enabled = analyticsSettings.ga4Enabled === true;
        const ga4Id = (analyticsSettings.ga4MeasurementId || '').trim();

                    if (ga4Enabled && ga4Id && GA4_MEASUREMENT_ID_PATTERN.test(ga4Id)) {
                      setGa4MeasurementId(ga4Id);
                      injectGa4Script(ga4Id);
                    } else {
                      setGa4MeasurementId(null);
                      ga4InitialSentRef.current = false;
                      removeGa4Script();
          if (ga4Enabled && ga4Id) {
            console.warn(
              `[GoogleTagManager] Invalid GA4 Measurement ID "${ga4Id}". Expected format "G-XXXXXXXXX". GA4 injection skipped.`
            );
          }
        }
      },
      (error) => {
        console.error(
          `[GoogleTagManager] Failed to load analytics settings from Firestore: ${error.message}. GTM/GA4 scripts will remain unchanged.`
        );
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!ga4MeasurementId || typeof window.gtag !== 'function') return;
    if (!ga4InitialSentRef.current) {
      ga4InitialSentRef.current = true;
      return;
    }
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location.pathname, location.search, ga4MeasurementId]);

  return null;
}
